import { useMemo } from 'react'

interface HourlyPoint {
  hour: number
  orders: number
  revenue: number
  riders: number
}

interface RevenueHeatMapProps {
  hourly: HourlyPoint[]
  loading?: boolean
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

const PALETTE = [
  '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa',
  '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a5f',
]

function getColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return PALETTE[0]
  const idx = Math.min(Math.floor((value / max) * (PALETTE.length - 1)), PALETTE.length - 1)
  return PALETTE[idx]
}

function formatHour(h: number): string {
  if (h === 0) return '12a'
  if (h < 12) return `${h}a`
  if (h === 12) return '12p'
  return `${h - 12}p`
}

export function RevenueHeatMap({ hourly, loading }: RevenueHeatMapProps) {
  const grid = useMemo(() => {
    if (!hourly || hourly.length === 0) return null

    const maxRevenue = Math.max(...hourly.map((h) => h.revenue || 0))
    const operatingHours = hourly.filter((h) => h.revenue > 0)
    const startHour = operatingHours.length > 0 ? Math.min(...operatingHours.map((h) => h.hour)) : 8
    const endHour = operatingHours.length > 0 ? Math.max(...operatingHours.map((h) => h.hour)) : 22
    const visibleHours = HOURS.filter((h) => h >= startHour && h <= endHour)

    return { maxRevenue, visibleHours, data: hourly }
  }, [hourly])

  if (loading) {
    return (
      <div className="mm-card-v3 p-5">
        <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-12 gap-1">
          {Array.from({ length: 48 }).map((_, i) => (
            <div key={i} className="h-8 bg-slate-100 rounded-sm animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (!grid || grid.visibleHours.length === 0) {
    return (
      <div className="mm-card-v3 p-5">
        <h3 className="font-display font-semibold text-sm text-ink-dark mb-3">Revenue Heat Map</h3>
        <div className="py-6 text-center text-sm text-ink-soft">
          No hourly data available for this event yet.
        </div>
      </div>
    )
  }

  return (
    <div className="mm-card-v3 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-sm text-ink-dark">Revenue Heat Map</h3>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-ink-hint">Low</span>
          <div className="flex gap-px">
            {PALETTE.map((color, i) => (
              <div
                key={i}
                className="h-3 w-3 rounded-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <span className="text-[10px] text-ink-hint">High</span>
        </div>
      </div>

      {/* Hour labels */}
      <div className="flex gap-1 mb-1 ml-0">
        {grid.visibleHours.map((h) => (
          <div
            key={h}
            className="flex-1 text-center text-[9px] font-mono text-ink-hint"
          >
            {h % 2 === 0 ? formatHour(h) : ''}
          </div>
        ))}
      </div>

      {/* Heat cells — single-day view since we have hourly for one event-day */}
      <div className="flex gap-1">
        {grid.visibleHours.map((h) => {
          const point = grid.data.find((p) => p.hour === h)
          const rev = point?.revenue || 0
          const color = getColor(rev, grid.maxRevenue)
          return (
            <div
              key={h}
              className="mm-heatmap-cell flex-1 h-10 relative group"
              style={{ backgroundColor: color }}
              title={`${formatHour(h)}: $${rev.toLocaleString()}`}
            >
              <div className="absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-ink-dark text-white text-[10px] font-mono whitespace-nowrap z-20 shadow-lg">
                {formatHour(h)} — ${rev.toLocaleString()}
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary stats below */}
      <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-hint">Peak Hour</div>
          <div className="mm-data text-sm font-bold text-ink-dark">
            {formatHour(grid.data.reduce((best, h) => h.revenue > (grid.data.find(p => p.hour === best)?.revenue || 0) ? h.hour : best, grid.data[0]?.hour || 0))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-hint">Peak Revenue</div>
          <div className="mm-data text-sm font-bold text-ink-dark">
            ${Math.max(...grid.data.map(h => h.revenue || 0)).toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-hint">Active Hours</div>
          <div className="mm-data text-sm font-bold text-ink-dark">
            {grid.visibleHours.length}
          </div>
        </div>
      </div>
    </div>
  )
}
